import {
  isOrganizationServiceVariant,
  isRootOrganizationServiceVariant,
  isTenantOrganizationServiceVariant,
} from '@/lib/service-variant-types'
import type { OrgTreeServiceInstance } from '@/services/org-tree'

export type NodeServicePermissionOption = {
  key: string
  instanceId: string
  variantId: string
  variantLabel: string
  instanceName: string
  levelVariantUniqueId?: string
  levelVariantName?: string
  hierarchyTierCode?: string
}

function normalizeVariantToken(value?: string | number | null): string {
  return String(value ?? '').trim().toLowerCase()
}

export function isBranchServicePermissionOption(option: Pick<NodeServicePermissionOption, 'variantId' | 'instanceId'>): boolean {
  const variantId = normalizeVariantToken(option.variantId)
  const instancePrefix = String(option.instanceId ?? '').trim().split('-')[0]
  return variantId === '103' || variantId === 'branch' || instancePrefix === '103'
}

export function toNodeServicePermissionOption(
  instance: OrgTreeServiceInstance | null | undefined,
): NodeServicePermissionOption | null {
  if (!instance) {
    return null
  }

  const instanceId = String(instance.instance_id ?? instance.id ?? '').trim()
  if (!instanceId) {
    return null
  }

  const variantId = String(
    instance.service_data?.variant_unique
    ?? (instance as { service_variant_unique_id?: string | number | null }).service_variant_unique_id
    ?? instance.service_variant
    ?? '',
  ).trim()
  const variantLabel = getServiceInstanceVariantDisplayName(instance)
  const instanceName = String(instance.name ?? instance.title ?? variantLabel).trim() || variantLabel
  const levelVariantUniqueId = String(instance.level_variant_unique_id ?? '').trim()
  const levelVariantName = String(instance.level_variant_name ?? '').trim()
  const hierarchyTierCode = String(instance.level_variant_vertical_system_code ?? '').trim()

  return {
    key: instanceId,
    instanceId,
    variantId,
    variantLabel,
    instanceName,
    ...(levelVariantUniqueId ? { levelVariantUniqueId } : {}),
    ...(levelVariantName ? { levelVariantName } : {}),
    ...(hierarchyTierCode ? { hierarchyTierCode } : {}),
  }
}

/**
 * Branch-admin Role Permissions service picker:
 * Branch + Org / Tenant ancestors (Org feature catalog via variant) + attached in-apps.
 *
 * Org/Tenant entries are listed for catalog switching; API calls should keep the Branch SI
 * and pass the selected org-scope variant (backend allows BRANCH → Org variant).
 */
export function buildBranchRolePermissionServiceOptions({
  nodeOptions,
  orgScopeOptions = [],
}: {
  nodeOptions: NodeServicePermissionOption[]
  orgScopeOptions?: NodeServicePermissionOption[]
}): NodeServicePermissionOption[] {
  const branchOptions = nodeOptions.filter((option) => isBranchServicePermissionOption(option))
  const inAppOptions = nodeOptions.filter((option) => {
    if (isBranchServicePermissionOption(option)) {
      return false
    }
    return !isOrganizationServiceVariant(option.variantId, option.variantLabel)
  })

  const preferredOrgScope = [...orgScopeOptions].sort((first, second) => {
    const firstIsRoot = isRootOrganizationServiceVariant(first.variantId, first.variantLabel)
    const secondIsRoot = isRootOrganizationServiceVariant(second.variantId, second.variantLabel)
    const firstIsTenant = isTenantOrganizationServiceVariant(first.variantId, first.variantLabel)
    const secondIsTenant = isTenantOrganizationServiceVariant(second.variantId, second.variantLabel)
    // Prefer root Org catalog (UpdateServiceInstance lives there) before tenant.
    if (firstIsRoot !== secondIsRoot) {
      return firstIsRoot ? -1 : 1
    }
    if (firstIsTenant !== secondIsTenant) {
      return firstIsTenant ? -1 : 1
    }
    return first.variantLabel.localeCompare(second.variantLabel)
  })

  // Preserve Branch → Org → Tenant → in-apps so the dialog defaults to Branch features.
  const ordered: NodeServicePermissionOption[] = []
  const seen = new Set<string>()
  for (const option of [...branchOptions, ...preferredOrgScope, ...inAppOptions]) {
    if (!option.instanceId || seen.has(option.instanceId)) {
      continue
    }
    seen.add(option.instanceId)
    ordered.push(option)
  }
  return ordered
}

/** Org/Tenant picker rows configure catalogs via variant on the branch SI — not as the request SI. */
export function resolveBranchRolePermissionRequestScope({
  selectedOption,
  branchInstanceId,
}: {
  selectedOption?: Pick<NodeServicePermissionOption, 'instanceId' | 'variantId' | 'variantLabel'> | null
  branchInstanceId?: string | null
}): { serviceInstanceId: string | null; serviceVariant: string | null } {
  const selectedInstanceId = String(selectedOption?.instanceId ?? '').trim() || null
  const selectedVariantId = String(selectedOption?.variantId ?? '').trim() || null
  const normalizedBranchInstanceId = String(branchInstanceId ?? '').trim() || null

  if (
    selectedOption
    && isOrganizationServiceVariant(selectedOption.variantId, selectedOption.variantLabel)
    && normalizedBranchInstanceId
  ) {
    return {
      serviceInstanceId: normalizedBranchInstanceId,
      serviceVariant: selectedVariantId,
    }
  }

  return {
    serviceInstanceId: selectedInstanceId || normalizedBranchInstanceId,
    serviceVariant: selectedVariantId,
  }
}

export function getServiceInstanceVariantDisplayName(
  instance: {
    service_variant_name?: string | null
    service_variant_label?: string | null
    service_variant_type?: string | null
    service_variant_service?: string | null
    service_variant?: string | number | null
    service_data?: OrgTreeServiceInstance['service_data']
  },
): string {
  const variantLabel = String(
    instance.service_variant_name
    ?? instance.service_variant_type
    ?? instance.service_variant_label
    ?? '',
  ).trim()

  if (variantLabel) {
    return variantLabel
  }

  return 'Service'
}

export function buildNodeServicePermissionOptions(
  instances: OrgTreeServiceInstance[],
): NodeServicePermissionOption[] {
  const byInstanceId = new Map<string, NodeServicePermissionOption>()

  for (const instance of instances) {
    const instanceId = String(instance.instance_id ?? instance.id ?? '').trim()
    if (!instanceId || byInstanceId.has(instanceId)) {
      continue
    }

    const variantId = String(
      instance.service_variant
      ?? instance.service_data?.variant_unique
      ?? '',
    ).trim()
    const variantLabel = getServiceInstanceVariantDisplayName(instance)
    const instanceName = String(instance.name ?? instance.title ?? variantLabel).trim() || variantLabel
    const levelVariantUniqueId = String(instance.level_variant_unique_id ?? '').trim()
    const levelVariantName = String(instance.level_variant_name ?? '').trim()
    const hierarchyTierCode = String(instance.level_variant_vertical_system_code ?? '').trim()

    byInstanceId.set(instanceId, {
      key: instanceId,
      instanceId,
      variantId,
      variantLabel,
      instanceName,
      ...(levelVariantUniqueId ? { levelVariantUniqueId } : {}),
      ...(levelVariantName ? { levelVariantName } : {}),
      ...(hierarchyTierCode ? { hierarchyTierCode } : {}),
    })
  }

  return Array.from(byInstanceId.values()).sort((first, second) => (
    first.variantLabel.localeCompare(second.variantLabel)
    || first.instanceName.localeCompare(second.instanceName)
  ))
}

type PostWorkspaceInstanceSource = {
  node_id?: string | number | null
  service_instance?: string | number | null
  service_instance_id?: string | number | null
  service_instance_name?: string | null
  service_variant_name?: string | null
  service_variant_type?: string | null
  service_variant_unique_id?: string | number | null
  level_variant_unique_id?: string | null
  level_variant_name?: string | null
  level_variant_vertical_system_code?: string | null
}

export function buildNodeServicePermissionOptionsFromPosts(
  posts: PostWorkspaceInstanceSource[] | null | undefined,
  nodeId?: string | number | null,
): NodeServicePermissionOption[] {
  const normalizedNodeId = String(nodeId ?? '').trim()
  if (!normalizedNodeId || !posts?.length) {
    return []
  }

  const instances = posts
    .filter((post) => String(post.node_id ?? '').trim() === normalizedNodeId)
    .map((post) => ({
      instance_id: String(post.service_instance ?? post.service_instance_id ?? '').trim(),
      service_variant: post.service_variant_unique_id,
      service_variant_name: post.service_variant_name ?? post.service_variant_type,
      name: post.service_instance_name,
      level_variant_unique_id: post.level_variant_unique_id,
      level_variant_name: post.level_variant_name,
      level_variant_vertical_system_code: post.level_variant_vertical_system_code,
    }))
    .filter((instance) => Boolean(String(instance.instance_id ?? '').trim())) as OrgTreeServiceInstance[]

  return buildNodeServicePermissionOptions(instances)
}

export function mergeNodeServicePermissionOptions(
  ...optionGroups: NodeServicePermissionOption[][]
): NodeServicePermissionOption[] {
  const byInstanceId = new Map<string, NodeServicePermissionOption>()

  for (const options of optionGroups) {
    for (const option of options) {
      if (!option.instanceId || byInstanceId.has(option.instanceId)) {
        continue
      }
      byInstanceId.set(option.instanceId, option)
    }
  }

  return Array.from(byInstanceId.values()).sort((first, second) => (
    first.variantLabel.localeCompare(second.variantLabel)
    || first.instanceName.localeCompare(second.instanceName)
  ))
}

export function resolveNodeServicePermissionOption(
  options: NodeServicePermissionOption[],
  instanceId?: string | null,
): NodeServicePermissionOption | null {
  const normalizedInstanceId = String(instanceId ?? '').trim()
  if (!normalizedInstanceId) {
    return options[0] ?? null
  }

  return options.find((option) => option.instanceId === normalizedInstanceId) ?? options[0] ?? null
}
